import { FC, useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { Button, Col, Row, Tooltip } from "antd"
import { Translated } from "../../utils/translated"
import {
  faChartColumn,
  faClipboardCheck,
  faPersonDigging,
  faUserGear,
} from "@fortawesome/free-solid-svg-icons"
import { FlexCol, FlexRow } from "../../components/commons-ts/common"
import { useUsercommContextBLE } from "../../usercomm/local/ble/usercommProviderBLE"
import {
  CENTRAL_CONTAINER_GAP,
  COLOR_BG_BLUE,
  COLOR_BG_DARK,
  COLOR_BG_GRAY,
  COLOR_BG_GREEN,
  COLOR_BG_LIGHT_BLUE,
  COLOR_BG_ORANGE,
  COLOR_BG_RED,
  colorHexToRgba,
} from "../../utils/utils"
import { Link } from "react-router-dom"
import { SwapOutlined } from "@ant-design/icons"
import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { useCloudUsercommStatsWS } from "../../usercomm/cloud/cloudUsercommAsyncRequestWS"
import {
  useUsercommGetStationConfigBLE,
  useUsercommStatsBLE,
} from "../../usercomm/local/ble/usercommAsyncRequestBLE"
import { BruteUserRankingWidget } from "../../components/commons-ts/bruteUserRankingWidget"
import { CardLink } from "../../utils/utils_tsx"
import dayjs from "dayjs"

const HOME_PAGE_BORDER_RADIUS = "12px"

const HomeUserPane: FC = () => {
  const { currentUser } = useSyncronizationContext()

  if (currentUser === null) {
    return null
  }

  const memoUserFullName = useMemo(() => {
    return `${currentUser.FirstName} ${currentUser.LastName}`
  }, [currentUser.FirstName, currentUser.LastName])

  return (
    <Row gutter={[10, 10]} justify="space-between" style={{ width: "100%" }}>
      <Col>
        <FlexCol
          style={{
            gap: 5,
          }}
        >
          {/* Hello, User Name */}
          <div>
            <Translated keyEn="Hello" />,{" "}
            <span style={{ color: COLOR_BG_BLUE, fontWeight: "bold" }}>{memoUserFullName}</span>
          </div>
          <div
            style={{
              fontSize: "0.9rem",
              color: COLOR_BG_GRAY,
            }}
          >
            {currentUser.Enterprise?.Name ?? null}
          </div>
        </FlexCol>
      </Col>
      {/* Brute Ranking */}
      <Col>
        <BruteUserRankingWidget user={currentUser} />
      </Col>
    </Row>
  )
}

const HomeDevicePane: FC = () => {
  // Device info (Cloud)
  const { currentRemoteDevice } = useSyncronizationContext()
  // Device info (BLE)
  const { bleIsConnected } = useUsercommContextBLE()
  const [stationConfig, getStationConfig] = useUsercommGetStationConfigBLE()

  const [localStats, getLocalStats] = useUsercommStatsBLE()
  const [maxGmax, setMaxGmax] = useState<number | null>(null)
  const [timesAboveGmaxLimit, setTimesAboveGmaxLimit] = useState<number | null>(null)

  useEffect(() => {
    if (bleIsConnected) {
      getStationConfig()
      getLocalStats(undefined)
    }
  }, [bleIsConnected])

  // Parse Stats
  useEffect(() => {
    if (localStats === null) {
      return
    }
    setMaxGmax(localStats.max_gmax)
    setTimesAboveGmaxLimit(localStats.count_gmax_above_limit)
  }, [localStats])

  const gmaxWidget = useMemo(() => {
    if (maxGmax === null || timesAboveGmaxLimit === null) {
      return null
    }
    return (
      <FlexCol
        style={{
          gap: 0,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FlexRow style={{ gap: 0 }}>
          <FlexCol
            style={{
              gap: 0,
              alignItems: "end",
              paddingRight: "5px",
            }}
          >
            <div
              style={{
                color: COLOR_BG_GRAY,
              }}
            >
              <Translated keyEn="Gmax" />
            </div>
            <div
              style={{
                color: COLOR_BG_BLUE,
                fontWeight: "bold",
                fontSize: "1.1rem",
              }}
            >
              {maxGmax.toFixed()}
            </div>
          </FlexCol>
          <FlexCol
            style={{
              gap: 0,
              alignItems: "start",
              paddingLeft: "5px",
              borderLeft: "1px solid " + COLOR_BG_GRAY,
            }}
          >
            <div
              style={{
                color: COLOR_BG_GRAY,
              }}
            >
              <Translated keyEn="Sup 300g" />
            </div>
            <div
              style={{
                color: timesAboveGmaxLimit > 0 ? COLOR_BG_RED : COLOR_BG_GREEN,
                fontWeight: "bold",
                fontSize: "1.1rem",
              }}
            >
              {timesAboveGmaxLimit}
            </div>
          </FlexCol>
        </FlexRow>
        <div
          style={{
            fontSize: "0.8rem",
            color: COLOR_BG_GRAY,
          }}
        >
          <Translated keyEn="Warranty void > 300g" />
        </div>
      </FlexCol>
    )
  }, [maxGmax, timesAboveGmaxLimit])

  const metrologyWidget = useMemo(() => {
    if (stationConfig === null) {
      return null
    }
    let dueDateStr: string | null = null
    let daysLeftStr: string | null = null
    if (stationConfig.next_metrology_date > 0) {
      let dueDate = dayjs(stationConfig.next_metrology_date)
      dueDateStr = dueDate.format("DD/MM/YY")

      let daysLeft = dueDate.diff(dayjs(), "day")
      if (daysLeft > 0) {
        daysLeftStr = daysLeft.toFixed(0)
      }
    }
    return (
      <FlexCol style={{ gap: 0, alignItems: "center", justifyContent: "center" }}>
        <div
          style={{
            color: COLOR_BG_GRAY,
          }}
        >
          <Translated keyEn="Metrology" />
        </div>
        <Tooltip
          overlay={
            daysLeftStr ? (
              <span>
                <Translated keyEn="In" /> {daysLeftStr} <Translated keyEn="days" />
              </span>
            ) : (
              <Translated keyEn="N/A" />
            )
          }
        >
          <div
            style={{
              color: daysLeftStr ? COLOR_BG_BLUE : COLOR_BG_RED,
              fontWeight: "bold",
              fontSize: "1.1rem",
            }}
          >
            {dueDateStr ? dueDateStr : <Translated keyEn="N/A" />}
          </div>
        </Tooltip>
        <div
          style={{
            fontSize: "0.8rem",
            color: COLOR_BG_GRAY,
          }}
        >
          <Translated keyEn="Every 12 months" />
        </div>
      </FlexCol>
    )
  }, [stationConfig])

  const deviceNameWidget = useMemo(() => {
    if (stationConfig === null) {
      return null
    }
    return (
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "12px 16px",
          color: COLOR_BG_BLUE,
          borderColor: COLOR_BG_GRAY,
          textAlign: "center",
        }}
      >
        <span>{stationConfig.serial_number}</span>
      </div>
    )
  }, [stationConfig, currentRemoteDevice])

  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: HOME_PAGE_BORDER_RADIUS,
        padding: "8px",
      }}
    >
      <FlexRow
        style={{
          backgroundColor: colorHexToRgba(COLOR_BG_LIGHT_BLUE, 0.2),
          borderRadius: HOME_PAGE_BORDER_RADIUS,
          padding: "8px",
          justifyContent: "end",
        }}
      >
        {stationConfig !== null ? (
          <Row gutter={[20, 10]} style={{ width: "100%" }} align="middle" justify="space-between">
            {/* Gmax, Sup 300g */}
            <Col>{gmaxWidget}</Col>
            {/* Metrology */}
            <Col>{metrologyWidget}</Col>
            {/* Device history */}
            <Col>
              <Link to="/settings/device#history">
                <Button
                  block
                  type="primary"
                  style={{
                    borderRadius: HOME_PAGE_BORDER_RADIUS,
                  }}
                >
                  <Translated keyEn="Carnet de Suivi" />
                </Button>
              </Link>
            </Col>
            {/* Device Name */}
            <Col>{deviceNameWidget}</Col>
          </Row>
        ) : (
          <Row gutter={[10, 10]} align="middle" justify="end">
            <Col>
              <div
                style={{
                  color: COLOR_BG_GRAY,
                  fontSize: "0.9rem",
                }}
              >
                <Translated keyEn="No device connected" />
              </div>
            </Col>
            <Col>
              <Link to="/connect">
                <Button icon={<SwapOutlined />}>
                  <Translated keyEn="Connect" />
                </Button>
              </Link>
            </Col>
          </Row>
        )}
      </FlexRow>
    </div>
  )
}

export const HomePage: FC = () => {
  return (
    <FlexCol
      style={{
        width: "100%",
        gap: CENTRAL_CONTAINER_GAP,
      }}
    >
      <Row>
        <Col xs={24}>
          <HomeUserPane />
        </Col>
      </Row>
      <Row
        gutter={[10, 10]}
        style={{
          width: "100%",
          marginBottom: "30px",
          backgroundColor: colorHexToRgba(COLOR_BG_GREEN, 0.2),
          borderRadius: HOME_PAGE_BORDER_RADIUS,
          minHeight: "80px",
        }}
      >
        <Col xs={0} md={1}></Col>
        <Col xs={24} md={17}>
          <Row gutter={[10, 0]} justify="end" align="bottom">
            <Col
              xs={24}
              style={{
                marginTop: "30px",
                marginBottom: "20px",
              }}
            >
              <span
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  color: COLOR_BG_GRAY,
                }}
              >
                <Translated keyEn="Soon, find all the information, alerts and notifications here" />
              </span>
            </Col>
            <Col xs={0} md={1}></Col>
            <Col xs={24}>
              <div
                style={{
                  marginBottom: "-30px",
                }}
              >
                <HomeDevicePane />
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={0} md={6}>
          <FlexRow
            style={{
              justifyContent: "end",
              alignItems: "end",
            }}
          >
            <img
              style={{ width: "200px", marginBottom: "-40px", marginRight: "-15px" }}
              src="/accueil-01.png"
            />
          </FlexRow>
        </Col>
      </Row>
      <Row gutter={[10, 20]} style={{ width: "100%", marginBottom: "10px" }}>
        <Col xs={24} md={12}>
          <CardLink
            key={"home-hic"}
            to="/sites/home"
            icon={<FontAwesomeIcon icon={faChartColumn} />}
            title={<Translated keyEn="Données HIC" />}
            description={
              <Translated keyEn="Accéder à l'ensemble de vos missions de capacité  amortissement sol sportif" />
            }
          />
        </Col>
        <Col xs={24} md={12}>
          <CardLink
            key={"home-regulatory"}
            to="/regulatory/home"
            icon={<FontAwesomeIcon icon={faClipboardCheck} />}
            title={<Translated keyEn="Contrôle d'Aires de Jeux" />}
            description={
              <Translated keyEn="Accéder à l'ensemble de vos missions de sécurité en Aire de jeux collectives" />
            }
          />
        </Col>
        <Col xs={24}>
          <CardLink
            key="home-physical-sites"
            to="/physical-sites"
            icon={<FontAwesomeIcon icon={faClipboardCheck} />}
            title={<span>Gestionnaire de Sites</span>}
            description={<span>Inventaire, notes et historique des inspections par lieu</span>}
          />
        </Col>
        {/* <Col xs={24} md={8}>
          <div>
            <CardLink
              key={"home-admin"}
              to="#"
              icon={<FontAwesomeIcon icon={faUserGear} />}
              title={<Translated keyEn="Gestionnaire" />}
              description={
                <Translated keyEn="Accéder à l'ensemble des équipements et de leur gestion" />
              }
            />
            <Tooltip overlay={<Translated keyEn="Under construction" />}>
              <div
                style={{
                  position: "absolute",
                  top: 3,
                  right: 7,
                }}
              >
                <FontAwesomeIcon
                  icon={faPersonDigging}
                  style={{
                    fontSize: "1.5rem",
                    color: COLOR_BG_ORANGE,
                  }}
                />
              </div>
            </Tooltip>
          </div>
        </Col> */}
      </Row>
    </FlexCol>
  )
}
